import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '../../api/endpoints';
import { DashboardData, PeriodStats, TimelinePoint, MarketplaceBreakdown, TopProduct, SyncHealth } from '../../types';
import {
  FiShoppingCart, FiClock, FiTruck, FiFileText, FiBox, FiAlertTriangle,
  FiDollarSign, FiCheckCircle, FiXCircle, FiRefreshCw, FiTrendingUp, FiTrendingDown,
  FiUploadCloud, FiActivity, FiPackage, FiStar, FiBarChart2, FiPieChart, FiCalendar,
  FiArrowRight, FiArrowUp, FiArrowDown, FiMinus, FiAlertCircle, FiZap,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { SkeletonStats, SkeletonCard } from '../../components/common/Skeleton';
import CountryFlag from '../../components/common/CountryFlag';
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, ComposedChart, Line,
} from 'recharts';

/* ===================================================================== */
/*  Types & helpers                                                       */
/* ===================================================================== */

type Period = 'week' | 'month' | 'year';

const STATUS_COLORS: Record<string, string> = {
  Draft: '#94a3b8', Submitted: '#f59e0b', Accepted: '#10b981', Rejected: '#ef4444',
  Paid: '#6366f1', New: '#3b82f6', Acknowledged: '#06b6d4', Confirmed: '#10b981',
  PartiallyConfirmed: '#f59e0b', Shipped: '#8b5cf6', PartiallyShipped: '#a78bfa',
  Delivered: '#10b981', Closed: '#64748b', Cancelled: '#dc2626',
  SellerCentral: '#3b82f6', Vendor: '#7c3aed',
};

const formatCurrency = (amount: number, currency = 'EUR') => {
  try {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency, maximumFractionDigits: 0 });
  } catch {
    return `${amount.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} ${currency}`;
  }
};

const formatNumber = (n: number) => n.toLocaleString('pl-PL');

const formatDate = (dateStr: string, withTime = false) => {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  if (!withTime) return date;
  return date + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
};

/** Tick formatter for the timeline x-axis. Adapts to granularity. */
const formatTick = (iso: string, granularity: string) => {
  const d = new Date(iso);
  if (granularity === 'month') {
    return d.toLocaleDateString('pl-PL', { month: 'short', year: '2-digit' });
  }
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
};

/* ===================================================================== */
/*  Period selector pill bar                                              */
/* ===================================================================== */

interface PeriodSelectorProps {
  value: Period;
  onChange: (p: Period) => void;
  disabled?: boolean;
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();
  const options: { key: Period; label: string }[] = [
    { key: 'week',  label: t('dashboard.periodWeek') },
    { key: 'month', label: t('dashboard.periodMonth') },
    { key: 'year',  label: t('dashboard.periodYear') },
  ];
  return (
    <div
      role="radiogroup"
      style={{
        display: 'inline-flex',
        padding: '3px',
        borderRadius: '10px',
        backgroundColor: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        gap: '2px',
      }}
    >
      {options.map(opt => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.key)}
            disabled={disabled}
            style={{
              padding: '7px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              backgroundColor: active ? 'var(--primary, #dc2626)' : 'transparent',
              color: active ? '#fff' : 'var(--text-muted)',
              transition: 'all 150ms ease',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

/* ===================================================================== */
/*  Delta chip — ↑12% green or ↓8% red                                    */
/* ===================================================================== */

interface DeltaChipProps {
  pct: number | null | undefined;
  /** When true, NEGATIVE delta is treated as good (e.g. "fewer overdue invoices"). */
  invertColors?: boolean;
}

const DeltaChip: React.FC<DeltaChipProps> = ({ pct, invertColors }) => {
  if (pct == null || !isFinite(pct)) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '2px 8px', borderRadius: '12px',
        backgroundColor: 'rgba(107,114,128,0.12)', color: 'var(--text-muted)',
        fontSize: '11px', fontWeight: 600,
      }}>
        <FiMinus size={11} /> —
      </span>
    );
  }
  const positive = pct >= 0;
  const isGood = invertColors ? !positive : positive;
  const color = pct === 0 ? 'var(--text-muted)' : (isGood ? '#10b981' : '#ef4444');
  const bg = pct === 0 ? 'rgba(107,114,128,0.12)' : (isGood ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)');
  const Icon = pct === 0 ? FiMinus : (positive ? FiArrowUp : FiArrowDown);
  return (
    <span title={`${pct.toFixed(1)}% vs poprzedni okres`} style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      padding: '2px 8px', borderRadius: '12px',
      backgroundColor: bg, color, fontSize: '11px', fontWeight: 700,
    }}>
      <Icon size={11} />
      {Math.abs(pct).toFixed(0)}%
    </span>
  );
};

/* ===================================================================== */
/*  Hero stat (big number + label + delta + tiny sparkline)               */
/* ===================================================================== */

interface HeroStatProps {
  label: string;
  value: string;
  subValue?: string;
  delta: number | null | undefined;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  sparkline?: TimelinePoint[];
  sparklineKey?: 'count' | 'value';
  onClick?: () => void;
  invertColors?: boolean;
}

const HeroStat: React.FC<HeroStatProps> = ({
  label, value, subValue, delta, icon, iconBg, iconColor,
  sparkline, sparklineKey = 'value', onClick, invertColors,
}) => (
  <div
    onClick={onClick}
    style={{
      backgroundColor: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: '14px',
      padding: '16px 18px',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 150ms ease',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      minWidth: 0,
    }}
    onMouseEnter={onClick ? e => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)';
    } : undefined}
    onMouseLeave={onClick ? e => {
      e.currentTarget.style.transform = '';
      e.currentTarget.style.boxShadow = '';
    } : undefined}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '10px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: iconBg, color: iconColor, fontSize: '18px', flexShrink: 0,
      }}>
        {icon}
      </div>
      <DeltaChip pct={delta} invertColors={invertColors} />
    </div>
    <div>
      <div style={{
        fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>
        {label}
      </div>
      <div style={{ fontSize: '26px', fontWeight: 800, color: 'var(--text)', lineHeight: 1.15, marginTop: '2px' }}>
        {value}
      </div>
      {subValue && (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
          {subValue}
        </div>
      )}
    </div>
    {sparkline && sparkline.length > 1 && (
      <div style={{ height: '32px', marginTop: '4px', marginLeft: '-4px', marginRight: '-4px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkline}>
            <defs>
              <linearGradient id={`spark-${label}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={iconColor} stopOpacity={0.4} />
                <stop offset="100%" stopColor={iconColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey={sparklineKey}
              stroke={iconColor}
              fill={`url(#spark-${label})`}
              strokeWidth={1.5}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

/* ===================================================================== */
/*  Section card wrapper                                                  */
/* ===================================================================== */

interface SectionProps {
  title: string;
  icon?: React.ReactNode;
  iconColor?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Optional fixed height so charts inside aren't compressed. */
  minHeight?: number;
}

const Section: React.FC<SectionProps> = ({ title, icon, iconColor = 'var(--text-muted)', action, children, minHeight }) => (
  <div style={{
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: '14px',
    overflow: 'hidden',
    minHeight,
    display: 'flex', flexDirection: 'column',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 18px', borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon && <span style={{ color: iconColor, display: 'inline-flex', fontSize: '15px' }}>{icon}</span>}
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
      </div>
      {action}
    </div>
    <div style={{ padding: '14px 18px', flex: 1 }}>
      {children}
    </div>
  </div>
);

/* ===================================================================== */
/*  Main page                                                              */
/* ===================================================================== */

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState<Period>('month');

  // Localized labels for status/channel keys (used in legends and recent activity)
  const STATUS_LABELS: Record<string, string> = useMemo(() => ({
    Draft: t('dashboard.statusDraft'),
    Submitted: t('dashboard.statusSubmitted'),
    Accepted: t('dashboard.statusAccepted'),
    Rejected: t('dashboard.statusRejected'),
    Paid: t('dashboard.statusPaid'),
    New: t('dashboard.statusNew'),
    Acknowledged: t('dashboard.statusAcknowledged'),
    Confirmed: t('dashboard.statusAccepted'),
    PartiallyConfirmed: t('dashboard.statusSubmitted'),
    Shipped: t('dashboard.statusShipped'),
    PartiallyShipped: t('dashboard.statusShipped'),
    Delivered: t('dashboard.statusDelivered'),
    Closed: t('dashboard.statusClosed'),
    Cancelled: t('dashboard.statusCancelled'),
    SellerCentral: 'Seller Central',
    Vendor: 'Vendor',
  }), [t]);

  /* ---- Data fetch ---- */

  const fetchData = useCallback(async (showRefresh = false, p: Period = period) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await dashboardApi.get({ period: p });
      setData(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('dashboard.fetchError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, t]);

  useEffect(() => {
    fetchData(false, period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  /* ---- Derived ---- */

  const handleActivityClick = (type: string, entityId: string | null) => {
    if (!entityId) return;
    switch (type) {
      case 'PurchaseOrder': navigate(`/purchase-orders/${entityId}`); break;
      case 'Shipment':       navigate(`/shipments/${entityId}`); break;
      case 'Invoice':        navigate(`/invoices/${entityId}`); break;
    }
  };

  /* ---- Loading / empty ---- */

  if (loading) {
    return (
      <div className="page-container page-container--wide">
        <div className="page-header"><h1>{t('dashboard.title')}</h1></div>
        <SkeletonStats count={4} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px', marginTop: '16px' }}>
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-container page-container--wide">
        <div className="alert alert-danger">{t('dashboard.noDataAlert')}</div>
      </div>
    );
  }

  const hasOverdue = data.overdueInvoiceCount > 0;
  const hasRejected = data.rejectedInvoiceCount > 0;

  /* ===================================================================== */
  /*  RENDER                                                                 */
  /* ===================================================================== */

  return (
    <div className="page-container page-container--wide" style={{ paddingBottom: '32px' }}>
      {/* ── Top bar: title + period selector + refresh ─────────────────── */}
      <div className="page-header" style={{ alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0 }}>{t('dashboard.title')}</h1>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            <FiCalendar style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            {formatDate(data.periodFrom)} &mdash; {formatDate(data.periodTo)}
            <span style={{ margin: '0 8px' }}>•</span>
            {t('dashboard.granularity')}: {data.timelineGranularity === 'day' ? t('dashboard.granDay') : data.timelineGranularity === 'week' ? t('dashboard.granWeek') : t('dashboard.granMonth')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <PeriodSelector value={period} onChange={setPeriod} disabled={refreshing} />
          <button
            className="btn btn-secondary"
            onClick={() => fetchData(true, period)}
            disabled={refreshing}
          >
            <FiRefreshCw className={refreshing ? 'spin-animation' : ''} />
            {refreshing ? t('dashboard.refreshing') : t('common.refresh')}
          </button>
        </div>
      </div>

      {/* ── Alert banners ────────────────────────────────────────────── */}
      {(hasOverdue || hasRejected) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {hasOverdue && (
            <div
              onClick={() => navigate('/invoices')}
              style={{
                cursor: 'pointer',
                padding: '14px 16px',
                borderRadius: '12px',
                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}
            >
              <FiAlertTriangle style={{ color: '#ef4444', fontSize: '24px', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#ef4444', fontSize: '14px' }}>
                  {t('dashboard.overdueInvoices', { count: data.overdueInvoiceCount })}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {t('dashboard.totalValue', { value: formatCurrency(data.overdueInvoiceValue) })}
                </div>
              </div>
              <FiArrowRight style={{ color: '#ef4444', flexShrink: 0 }} />
            </div>
          )}
          {hasRejected && (
            <div
              onClick={() => navigate('/invoices')}
              style={{
                cursor: 'pointer',
                padding: '14px 16px',
                borderRadius: '12px',
                backgroundColor: 'rgba(245, 158, 11, 0.08)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}
            >
              <FiXCircle style={{ color: '#f59e0b', fontSize: '24px', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#f59e0b', fontSize: '14px' }}>
                  {t('dashboard.rejectedInvoices', { count: data.rejectedInvoiceCount })}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {t('dashboard.rejectedHint')}
                </div>
              </div>
              <FiArrowRight style={{ color: '#f59e0b', flexShrink: 0 }} />
            </div>
          )}
        </div>
      )}

      {/* ── Hero stats — period-scoped ───────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '14px',
        marginBottom: '20px',
      }}>
        <HeroStat
          label={t('dashboard.heroOrders')}
          value={formatNumber(data.ordersInPeriod.count)}
          subValue={formatCurrency(data.ordersInPeriod.value)}
          delta={data.ordersInPeriod.countChangePercent}
          icon={<FiShoppingCart />}
          iconBg="rgba(59, 130, 246, 0.12)"
          iconColor="#3b82f6"
          sparkline={data.ordersTimeline}
          sparklineKey="value"
          onClick={() => navigate('/purchase-orders')}
        />
        <HeroStat
          label={t('dashboard.heroInvoices')}
          value={formatNumber(data.invoicesInPeriod.count)}
          subValue={formatCurrency(data.invoicesInPeriod.value)}
          delta={data.invoicesInPeriod.countChangePercent}
          icon={<FiFileText />}
          iconBg="rgba(16, 185, 129, 0.12)"
          iconColor="#10b981"
          sparkline={data.invoicesTimeline}
          sparklineKey="value"
          onClick={() => navigate('/invoices')}
        />
        <HeroStat
          label={t('dashboard.heroSettlements')}
          value={formatCurrency(data.settlementsInPeriod.value)}
          subValue={t('dashboard.heroTransactions', { count: data.settlementsInPeriod.count })}
          delta={data.settlementsInPeriod.valueChangePercent}
          icon={<FiDollarSign />}
          iconBg="rgba(124, 58, 237, 0.12)"
          iconColor="#7c3aed"
          sparkline={data.settlementsTimeline}
          sparklineKey="value"
          onClick={() => navigate('/settlements')}
        />
        <HeroStat
          label={t('dashboard.heroShipments')}
          value={formatNumber(data.shipmentsInPeriod.count)}
          subValue={t('dashboard.heroActiveNow', { count: data.activeShipments })}
          delta={data.shipmentsInPeriod.countChangePercent}
          icon={<FiTruck />}
          iconBg="rgba(245, 158, 11, 0.12)"
          iconColor="#f59e0b"
          onClick={() => navigate('/fba-fulfilled')}
        />
      </div>

      {/* ── Trend chart (orders + revenue) ───────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
        gap: '14px',
        marginBottom: '20px',
      }}>
        <Section
          title={t('dashboard.orderRevenueTrend')}
          icon={<FiBarChart2 />}
          iconColor="#3b82f6"
          minHeight={340}
        >
          {data.ordersTimeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={data.ordersTimeline}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="#3b82f6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                  tickFormatter={(v) => formatTick(v, data.timelineGranularity)}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <Tooltip
                  labelFormatter={(label) => formatDate(String(label))}
                  formatter={(value: any, name: any) =>
                    name === 'value' ? formatCurrency(value) : formatNumber(value)
                  }
                  contentStyle={{
                    backgroundColor: 'var(--bg-elevated)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                  }}
                />
                <Legend
                  formatter={(v) => v === 'value' ? t('dashboard.revenue') : t('dashboard.orderCount')}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  fill="url(#revenueGrad)"
                  strokeWidth={2}
                />
                <Bar
                  yAxisId="right"
                  dataKey="count"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                  barSize={data.ordersTimeline.length > 30 ? 4 : 12}
                  opacity={0.75}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState text={t('common.noData')} />
          )}
        </Section>

        {/* Channel mix (pie) */}
        <Section
          title={t('dashboard.channelMix')}
          icon={<FiPieChart />}
          iconColor="#7c3aed"
          minHeight={340}
        >
          {data.ordersByChannel.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={data.ordersByChannel.map(c => ({
                    name: STATUS_LABELS[c.channel] || c.channel,
                    value: c.count,
                    fill: STATUS_COLORS[c.channel] || '#94a3b8',
                  }))}
                  cx="50%" cy="45%"
                  innerRadius={50} outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {data.ordersByChannel.map((c, i) => (
                    <Cell key={i} fill={STATUS_COLORS[c.channel] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState text={t('common.noData')} />
          )}
        </Section>
      </div>

      {/* ── Funnels: invoice + SAP sync ──────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '14px',
        marginBottom: '20px',
      }}>
        <Section title={t('dashboard.invoiceFunnel')} icon={<FiFileText />} iconColor="#10b981">
          <FunnelStrip
            steps={[
              { label: t('dashboard.statusDraft'),     count: data.draftInvoiceCount,     color: '#94a3b8' },
              { label: t('dashboard.statusSubmitted'), count: data.submittedInvoiceCount, color: '#f59e0b' },
              { label: t('dashboard.statusAccepted'),  count: data.acceptedInvoiceCount,  color: '#10b981' },
              { label: t('dashboard.statusPaid'),      count: data.paidInvoiceCount,      color: '#6366f1' },
              { label: t('dashboard.statusRejected'),  count: data.rejectedInvoiceCount,  color: '#ef4444' },
            ]}
            onClick={() => navigate('/invoices')}
          />
        </Section>
        <Section title={t('dashboard.sapFunnel')} icon={<FiUploadCloud />} iconColor="#3b82f6">
          <FunnelStrip
            steps={[
              { label: t('orders.sapSyncNotSynced'), count: data.sapNotSyncedCount, color: '#94a3b8' },
              { label: t('orders.sapSyncQueued'),    count: data.sapQueuedCount,    color: '#f59e0b' },
              { label: t('orders.sapSyncSynced'),    count: data.sapSyncedCount,    color: '#10b981' },
            ]}
            onClick={() => navigate('/purchase-orders')}
          />
        </Section>
      </div>

      {/* ── Marketplaces breakdown (orders + settlements) ────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: '14px',
        marginBottom: '20px',
      }}>
        <Section title={t('dashboard.ordersByMarketplace')} icon={<FiShoppingCart />} iconColor="#3b82f6">
          <MarketplaceList breakdown={data.ordersByMarketplace} />
        </Section>
        <Section title={t('dashboard.settlementsByMarketplace')} icon={<FiDollarSign />} iconColor="#7c3aed">
          <MarketplaceList breakdown={data.settlementsByMarketplace} />
        </Section>
      </div>

      {/* ── Top products + Sync health ───────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
        gap: '14px',
        marginBottom: '20px',
      }}>
        <Section title={t('dashboard.topProducts')} icon={<FiStar />} iconColor="#f59e0b">
          <TopProductsTable products={data.topProducts} onClick={() => navigate('/products')} />
        </Section>
        <Section title={t('dashboard.syncHealth')} icon={<FiZap />} iconColor="#10b981">
          <SyncHealthList health={data.syncHealth} onClick={() => navigate('/sync-logs')} />
        </Section>
      </div>

      {/* ── Recent activity ───────────────────────────────────────────── */}
      <Section title={t('dashboard.recentActivity')} icon={<FiActivity />} iconColor="#6366f1">
        {data.recentActivity.length > 0 ? (
          <RecentActivityList items={data.recentActivity} onClick={handleActivityClick} t={t} />
        ) : (
          <EmptyState text={t('dashboard.noActivity')} />
        )}
      </Section>
    </div>
  );
};

/* ===================================================================== */
/*  Sub-components                                                         */
/* ===================================================================== */

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    textAlign: 'center', padding: '40px 20px',
    color: 'var(--text-muted)', fontSize: '13px',
  }}>
    {text}
  </div>
);

interface FunnelStripProps {
  steps: { label: string; count: number; color: string }[];
  onClick?: () => void;
}

const FunnelStrip: React.FC<FunnelStripProps> = ({ steps, onClick }) => {
  const total = Math.max(1, steps.reduce((a, b) => a + b.count, 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} onClick={onClick}>
      {steps.map((s, i) => {
        const pct = (s.count / total) * 100;
        return (
          <div key={i} style={{ cursor: onClick ? 'pointer' : 'default' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: '4px',
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
                {s.label}
              </span>
              <span style={{ fontSize: '12px', fontWeight: 700, color: s.color }}>
                {s.count.toLocaleString('pl-PL')}
                <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>
                  {pct.toFixed(0)}%
                </span>
              </span>
            </div>
            <div style={{
              height: '8px',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-secondary, rgba(107,114,128,0.12))',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${pct}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${s.color}, ${s.color}cc)`,
                transition: 'width 300ms ease',
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface MarketplaceListProps {
  breakdown: MarketplaceBreakdown[];
}

const MarketplaceList: React.FC<MarketplaceListProps> = ({ breakdown }) => {
  if (breakdown.length === 0) return <EmptyState text="—" />;
  const max = Math.max(...breakdown.map(b => Math.abs(b.value)), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '320px', overflowY: 'auto' }}>
      {breakdown.slice(0, 12).map((b, i) => {
        const pct = (Math.abs(b.value) / max) * 100;
        const negative = b.value < 0;
        return (
          <div key={i}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              marginBottom: '4px', fontSize: '12px',
            }}>
              <CountryFlag countryCode={b.countryCode} size={14} />
              <span style={{ color: 'var(--text)', fontWeight: 600, flex: 1, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={b.marketplace}>
                {b.countryCode || b.marketplace}
              </span>
              <span style={{
                color: negative ? '#ef4444' : 'var(--text)',
                fontWeight: 700, fontVariantNumeric: 'tabular-nums',
              }}>
                {formatCurrency(b.value)}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: '11px', minWidth: '38px', textAlign: 'right' }}>
                {b.count.toLocaleString('pl-PL')}
              </span>
            </div>
            <div style={{
              height: '4px', borderRadius: '2px',
              backgroundColor: 'var(--bg-secondary, rgba(107,114,128,0.12))', overflow: 'hidden',
            }}>
              <div style={{
                width: `${pct}%`, height: '100%',
                backgroundColor: negative ? '#ef4444' : '#3b82f6',
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface TopProductsTableProps {
  products: TopProduct[];
  onClick?: () => void;
}

const TopProductsTable: React.FC<TopProductsTableProps> = ({ products, onClick }) => {
  const { t } = useTranslation();
  if (products.length === 0) return <EmptyState text="—" />;
  return (
    <div style={{ overflowX: 'auto', maxHeight: '360px', overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg-elevated)' }}>
          <tr>
            <th style={thStyle}>#</th>
            <th style={thStyle}>{t('orders.product')}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{t('orders.ordered')}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{t('dashboard.revenue')}</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => (
            <tr key={i} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
              <td style={tdStyle}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '22px', height: '22px', borderRadius: '50%',
                  backgroundColor: i < 3 ? '#f59e0b' : 'var(--bg-secondary, rgba(107,114,128,0.12))',
                  color: i < 3 ? '#fff' : 'var(--text-muted)',
                  fontSize: '11px', fontWeight: 700,
                }}>
                  {i + 1}
                </span>
              </td>
              <td style={tdStyle}>
                <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                  {p.productTitle || p.sku || p.asin || '—'}
                </div>
                {(p.sku || p.asin) && (
                  <div style={{
                    fontSize: '11px', color: 'var(--text-muted)',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {[p.sku, p.asin].filter(Boolean).join(' · ')}
                  </div>
                )}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                {formatNumber(p.orderedQuantity)}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right', color: '#10b981', fontWeight: 700 }}>
                {formatCurrency(p.revenue)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '11px', fontWeight: 700,
  color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: '1px solid var(--border)',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  verticalAlign: 'middle',
  color: 'var(--text)',
};

interface SyncHealthListProps {
  health: SyncHealth[];
  onClick?: () => void;
}

const SyncHealthList: React.FC<SyncHealthListProps> = ({ health, onClick }) => {
  const { t } = useTranslation();
  if (health.length === 0) return <EmptyState text={t('dashboard.noSyncs')} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {health.slice(0, 8).map((h, i) => {
        const isOk = h.lastStatus === 'Completed' && h.recentErrorCount === 0;
        const isFailing = h.recentErrorCount > 0 || h.lastStatus === 'Failed';
        const color = isFailing ? '#ef4444' : isOk ? '#10b981' : '#f59e0b';
        const bg = isFailing ? 'rgba(239,68,68,0.08)' : isOk ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)';
        return (
          <div
            key={i}
            onClick={onClick}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 12px',
              borderRadius: '8px',
              backgroundColor: bg,
              border: `1px solid ${color}40`,
              cursor: onClick ? 'pointer' : 'default',
            }}
          >
            <span style={{
              width: '8px', height: '8px', borderRadius: '50%',
              backgroundColor: color, flexShrink: 0,
              boxShadow: `0 0 6px ${color}`,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '12px', fontWeight: 600, color: 'var(--text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {h.source}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                {h.lastSuccessfulSync
                  ? t('dashboard.lastSync', { date: formatDate(h.lastSuccessfulSync, true) })
                  : t('dashboard.neverSynced')}
                {h.recentErrorCount > 0 && (
                  <span style={{ color: '#ef4444', fontWeight: 600, marginLeft: '8px' }}>
                    {t('dashboard.errors24h', { count: h.recentErrorCount })}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface RecentActivityListProps {
  items: { type: string; description: string; timestamp: string; entityId: string | null }[];
  onClick: (type: string, entityId: string | null) => void;
  t: any;
}

const RecentActivityList: React.FC<RecentActivityListProps> = ({ items, onClick, t }) => (
  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
    {items.map((a, i) => {
      const tagColor =
        a.type === 'PurchaseOrder' ? '#3b82f6' :
        a.type === 'Shipment'      ? '#f59e0b' :
        a.type === 'Invoice'       ? '#10b981' :
                                     '#8b5cf6';
      const tagBg =
        a.type === 'PurchaseOrder' ? 'rgba(59,130,246,0.12)' :
        a.type === 'Shipment'      ? 'rgba(245,158,11,0.12)' :
        a.type === 'Invoice'       ? 'rgba(16,185,129,0.12)' :
                                     'rgba(139,92,246,0.12)';
      const tagLabel =
        a.type === 'PurchaseOrder' ? 'PO' :
        a.type === 'Shipment'      ? t('dashboard.activityShipment') :
        a.type === 'Invoice'       ? t('dashboard.activityInvoice') :
                                     'Sync';
      return (
        <li
          key={i}
          onClick={() => onClick(a.type, a.entityId)}
          style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '10px 12px',
            borderRadius: '8px',
            cursor: a.entityId ? 'pointer' : 'default',
            transition: 'background 150ms ease',
          }}
          onMouseEnter={a.entityId ? e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(107,114,128,0.08))' : undefined}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: '2px 8px',
            borderRadius: '8px',
            fontSize: '10px', fontWeight: 700,
            backgroundColor: tagBg, color: tagColor,
            flexShrink: 0,
          }}>
            {tagLabel}
          </span>
          <span style={{ flex: 1, fontSize: '13px', color: 'var(--text)', minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {a.description}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {formatDate(a.timestamp, true)}
          </span>
        </li>
      );
    })}
  </ul>
);

export default DashboardPage;
